/** Configs de Conjuntos (Caminhões, Máquinas, Utilitários) e de Duplas.
 *
 *  Desde 04/09/2026 (Samuel) Conjuntos é UMA tela com três abas em Logística:
 *  Caminhões, o que vem do Protheus; Máquinas, criadas pelo usuário com operador,
 *  máquina e plataforma; Utilitários, operador mais veículo utilitário. Duplas
 *  foi para Cadastros. */
/* eslint-disable react-refresh/only-export-components -- configs co-locam Fields propositalmente */
import { FrotaProtheusBanner } from '../../../shared/protheus/ProtheusBanner'
import { useState } from 'react'
import { z } from 'zod'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Typography from '@mui/material/Typography'
import { RHFText, RHFSelect, RHFSwitch, RHFMultiAutocomplete, type Option } from '../../../shared/form/fields'
import { FleetIcon, FleetPlate } from '../../../shared/components/FleetIcon'
import { StatusChip } from '../../../shared/components/StatusChip'
import { SituacaoPessoa } from '../../../shared/components/SituacaoPessoa'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { comporFiltros, makeSituacaoFilter, useCategoryOptions, SEM_MODALIDADE } from './_shared'
import { CARRETA, CARROCERIAS, CARROCERIAS_PARAM, MAQUINA } from '../../../shared/fleetTipos'
import { CATALOG_STALE_TIME } from '../../../core/query/queryClient'
import {
  customerOptionsLoader,
  empresaPaiDoCliente,
  driverOptionsLoader,
  fleetOptionsLoader,
  machineOptionsLoader,
  operatorOptionsLoader,
  personnelOptionsLoader,
  platformOptionsLoader,
  staffUserOptionsLoader,
  trailerOptionsLoader,
  truckOptionsLoader,
  utilityVehicleOptionsLoader,
} from '../../../shared/form/catalogLoaders'
import type { ResourceConfig, ResourceFilters } from '../config'
import {
  duplasResource, conjuntosCaminhaoResource, conjuntosMaquinarioResource, conjuntosUtilitarioResource,
  personnelResource, fleetResource, personnelTodasDuplasResource, fleetTodasDuplasResource,
  fleetCategoriesResource, customersResource, usersResource,
} from '../resources'
import type { Categoria, Dupla, ConjuntoCaminhao, ConjuntoMaquinario, ConjuntoUtilitario } from '../../../core/api/types'
import { useDeclarativeText } from '../../../shared/i18n/declarativeText'
import { useI18n } from '../../../core/i18n/useI18n'
import { categoryLabel } from '../../../shared/categoryLabels'
import { DeclarativeText } from '../../../shared/components/DeclarativeText'

const withEmpty = (o: Option[]): Option[] => [{ value: '', label: '—' }, ...o]
const CAT = { staleTime: CATALOG_STALE_TIME }

// As opções que dão nome ao que o conjunto já tem saem sem o recorte de dupla
// (15/10/2026): Conjuntos não é Frente nem Painel de Descarga.
function usePersonOptions(category: string): Option[] {
  const q = personnelTodasDuplasResource.useList({ per_page: 100, category }, CAT)
  return (q.data?.items ?? []).map((p) => ({ value: p.uuid, label: p.name }))
}
function useFleetOptions(category: string): Option[] {
  const { t } = useI18n()
  const q = fleetTodasDuplasResource.useList({ per_page: 100, category }, CAT)
  return (q.data?.items ?? []).map((f) => ({
    value: f.uuid,
    label: (f.plate || f.code || f.name || f.uuid) as string,
    hint: f.category ? categoryLabel(f.category, t) : undefined,
    icon: <FleetIcon category={f.category} />,
  }))
}
// ── Opções de escopo da dupla (multi-select por aba) ─────────────────
type OptState = { options: Option[]; loading: boolean }
function useCustomerScopeOptions(): OptState {
  const q = customersResource.useList({ per_page: 100 }, CAT)
  const options = (q.data?.items ?? []).map((c) => ({
    value: c.uuid, label: c.trade_name || c.legal_name, sublabel: empresaPaiDoCliente(c),
  }))
  return { options, loading: q.isLoading }
}
function useFleetScopeOptions(): OptState {
  const q = fleetResource.useList({ per_page: 100 }, CAT)
  const options = (q.data?.items ?? []).map((f) => ({ value: f.uuid, label: (f.plate || f.code || f.name || f.uuid) as string }))
  return { options, loading: q.isLoading }
}
function usePersonnelScopeOptions(): OptState {
  const q = personnelResource.useList({ per_page: 100 }, CAT)
  const options = (q.data?.items ?? []).map((p) => ({ value: p.uuid, label: p.name }))
  return { options, loading: q.isLoading }
}
function useStaffOptions(): OptState {
  const q = usersResource.useList({ per_page: 100 }, CAT)
  const options = (q.data?.items ?? [])
    .filter((u) => u.is_staff)
    .map((u) => ({ value: u.uuid, label: u.name || u.email }))
  return { options, loading: q.isLoading }
}

/** Máquinas = veículos cuja Modalidade no Protheus é Máquina. */
function useMachineOptions(): Option[] {
  const { t } = useI18n()
  const machines = fleetTodasDuplasResource.useList({ per_page: 100, category: MAQUINA }, CAT)
  return (machines.data?.items ?? []).map(
    (f) => ({
      value: f.uuid,
      label: (f.code || f.plate || f.name || f.uuid) as string,
      hint: f.category ? categoryLabel(f.category, t) : undefined,
      icon: <FleetIcon category={f.category} />,
    }),
  )
}

// ── Duplas ───────────────────────────────────────────────────────────
const uuidList = z.array(z.string()).optional()
const duplaSchema = z.object({
  name: z.string().min(1, 'Informe o nome'), code: z.string().optional(),
  active: z.boolean().optional(), notes: z.string().optional(),
  customer_uuids: uuidList, fleet_uuids: uuidList,
  personnel_uuids: uuidList, membro_uuids: uuidList,
})
function DuplaFields() {
  const [tab, setTab] = useState(0)
  const tt = useDeclarativeText()
  const clientes = useCustomerScopeOptions()
  const frotas = useFleetScopeOptions()
  const pessoal = usePersonnelScopeOptions()
  const membros = useStaffOptions()
  return (
    <>
      <RHFText name="name" label="Nome da dupla" required />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <RHFText name="code" label="Código" />
        <RHFSwitch name="active" label="Ativa" />
      </Box>
      <RHFText name="notes" label="Observações" multiline rows={2} />

      <Box sx={{ mt: 1, borderTop: 1, borderColor: 'divider', pt: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label={tt('Clientes')} />
          <Tab label={tt('Frotas')} />
          <Tab label={tt('Pessoal')} />
          <Tab label={tt('Membros')} />
        </Tabs>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {tab === 0 && (
            <>
              <RHFMultiAutocomplete
                name="customer_uuids" label="Clientes no escopo"
                loadOptions={customerOptionsLoader} selectedOptions={clientes.options} loading={clientes.loading}
                asyncKey="dupla:customers"
                placeholder="Buscar cliente…"
              />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {tt('Ao vincular um cliente, seus contratos, frotas e pessoal entram no escopo da dupla automaticamente (por cliente). As abas Frotas e Pessoal servem para itens avulsos.')}
              </Typography>
            </>
          )}
          {tab === 1 && (
            <RHFMultiAutocomplete
              name="fleet_uuids" label="Frotas avulsas"
              loadOptions={fleetOptionsLoader} selectedOptions={frotas.options} loading={frotas.loading}
              asyncKey="dupla:fleet"
              placeholder="Buscar frota (placa/código)…"
            />
          )}
          {tab === 2 && (
            <RHFMultiAutocomplete
              name="personnel_uuids" label="Pessoal avulso"
              loadOptions={personnelOptionsLoader} selectedOptions={pessoal.options} loading={pessoal.loading}
              asyncKey="dupla:personnel"
              placeholder="Buscar pessoa…"
            />
          )}
          {tab === 3 && (
            <RHFMultiAutocomplete
              name="membro_uuids" label="Membros da dupla (backoffice)"
              loadOptions={staffUserOptionsLoader} selectedOptions={membros.options} loading={membros.loading}
              asyncKey="dupla:staff"
              placeholder="Buscar usuário do backoffice…"
            />
          )}
        </Box>
      </Box>
    </>
  )
}
export const duplasConfig: ResourceConfig<Dupla> = {
  title: 'Duplas', singular: 'Dupla', eyebrow: 'Cadastros', exportPrefix: 'duplas',
  basePath: '/logistica/cadastros/duplas',
  createPermission: 'sets.create', editPermission: 'sets.edit', deletePermission: 'sets.delete',
  // Quem está em qual dupla vem do código de dupla do ERP, gravado pela
  // sincronização de pessoas e de frota.
  protheusSync: ['motoristas', 'frota'], Banner: FrotaProtheusBanner,
  labelOf: (d) => d.name,
  resource: duplasResource,
  columns: [
    { key: 'name', header: 'Nome' },
    { key: 'customers', header: 'Clientes', render: (d) => (d.customers ?? []).map((c) => c.name).join(', ') || '—' },
    { key: 'membros', header: 'Membros', render: (d) => (d.membros ?? []).map((m) => m.user?.name).filter(Boolean).join(', ') || '—' },
    { key: 'active', header: 'Ativa', render: (d) => <DeclarativeText value={d.active ? 'Sim' : 'Não'} /> },
  ],
  searchable: true,
  schema: duplaSchema,
  emptyForm: {
    name: '', code: '', active: true, notes: '',
    customer_uuids: [], fleet_uuids: [], personnel_uuids: [], membro_uuids: [],
  },
  toForm: (d) => ({
    name: d.name, code: d.code ?? '', active: d.active, notes: d.notes ?? '',
    customer_uuids: (d.customers ?? []).map((c) => c.uuid),
    fleet_uuids: (d.frotas ?? []).map((f) => f.uuid),
    personnel_uuids: (d.pessoal ?? []).map((p) => p.uuid),
    membro_uuids: (d.membros ?? []).map((m) => m.user?.uuid).filter((x): x is string => Boolean(x)),
  }),
  toBody: (v) => ({
    name: v.name, code: v.code || null, active: Boolean(v.active), notes: v.notes || null,
    customer_uuids: v.customer_uuids ?? [],
    fleet_uuids: v.fleet_uuids ?? [],
    personnel_uuids: v.personnel_uuids ?? [],
    membro_uuids: v.membro_uuids ?? [],
  }),
  Fields: DuplaFields,
}

// ── Caminhões ────────────────────────────────────────────────────────
const caminhaoSchema = z.object({
  driver_uuid: z.string().optional(), truck_uuid: z.string().optional(),
  trailer1_uuid: z.string().optional(), trailer2_uuid: z.string().optional(),
  name: z.string().optional(), active: z.boolean().optional(), notes: z.string().optional(),
})

/** Cabeçalho do formulário de conjunto: por que tudo está travado.
 *  Mesmo papel do OrigemProtheusCard da Frota. Desde 14/08 o ERP manda também o
 *  motorista titular, e o conjunto virou espelho por inteiro; onde o titular vem
 *  vazio na origem, o vínculo local fica congelado como está. */
function ConjuntoEspelhoCard() {
  const tt = useDeclarativeText()
  return (
    <Card variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', bgcolor: 'action.hover' }}>
      <StatusChip label="Protheus" tone="info" />
      <Typography variant="body2" color="text.secondary">
        {tt('Conjunto mantido pelo ERP: cavalo, carretas e motorista são reescritos a cada sincronização. Apenas as observações são editáveis.')}
      </Typography>
    </Card>
  )
}

function CaminhaoFields() {
  const drivers = withEmpty(usePersonOptions('driver'))
  // O cavalo é tipado pela composição que puxa; a carreta tem tipo próprio. Por
  // isso cada slot lista o seu universo, sem risco de trocar um pelo outro.
  const cavalos = withEmpty(useFleetOptions(CARROCERIAS_PARAM))
  const carretas = withEmpty(useFleetOptions(CARRETA))
  return (
    <>
      <ConjuntoEspelhoCard />
      <RHFSelect name="driver_uuid" label="Motorista" loadOptions={driverOptionsLoader} selectedOptions={drivers} asyncKey="conjunto:drivers" placeholder="Buscar motorista…" disabled />
      <RHFSelect name="truck_uuid" label="Cavalo" loadOptions={truckOptionsLoader} selectedOptions={cavalos} asyncKey="conjunto:trucks" placeholder="Buscar placa do cavalo…" disabled />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <RHFSelect name="trailer1_uuid" label="Carreta 1" loadOptions={trailerOptionsLoader} selectedOptions={carretas} asyncKey="conjunto:trailers" placeholder="Buscar placa da carreta…" disabled />
        <RHFSelect name="trailer2_uuid" label="Carreta 2" loadOptions={trailerOptionsLoader} selectedOptions={carretas} asyncKey="conjunto:trailers" placeholder="Buscar placa da carreta…" disabled />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
        <RHFText name="name" label="Nome, derivado de motorista e placa" disabled />
        <RHFSwitch name="active" label="Ativo" disabled />
      </Box>
      <RHFText name="notes" label="Observações" multiline rows={2} />
    </>
  )
}
/** A modalidade na LINHA: ícone do tipo mais o nome, como a frota mostra em toda
 *  tela. Cavalo sem categoria classificada aparece vazio em vez de inventar tipo. */
function ModalidadeDoCavalo({ categoria }: { categoria?: Categoria | null }) {
  const { t } = useI18n()
  if (!categoria) return <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <FleetIcon category={categoria} />
      <span>{categoryLabel(categoria, t)}</span>
    </Box>
  )
}

/** Modalidade do conjunto de caminhão. Ela vem SEMPRE do cavalo (Samuel,
 *  17/09/2026): é ele que carrega a carroceria, e é por ela que a operação separa
 *  prancha de granel. A carreta tem categoria própria e não responde por isso.
 *
 *  Em destaque ficam as seis carrocerias, que é o vocabulário do dia a dia. Duas
 *  opções fecham a fileira, e elas existem porque sem elas a soma dos botões nunca
 *  chegava ao contador que fica logo acima: "Outras" recolhe toda modalidade que o
 *  ERP publica fora das carrocerias, como Fazenda, Frota Leve e Outros, e "Sem
 *  modalidade" é o traço, do conjunto sem cavalo. Juntas, elas cobrem a grade
 *  inteira, e "Outras" acompanha sozinha uma modalidade nova do Protheus, porque
 *  nasce do catálogo e não de uma lista escrita aqui. */
function useModalidadeDoCavalo(): ResourceFilters {
  const [modalidade, setModalidade] = useState('')
  const tt = useDeclarativeText()
  const { t } = useI18n()
  const catalogo = useCategoryOptions(fleetCategoriesResource)
  const carrocerias = new Set<string>(CARROCERIAS)
  const outras = catalogo.map((o) => o.value).filter((code) => !carrocerias.has(code)).join(',')
  return {
    params: { truck_category: modalidade || undefined },
    node: (
      <ToggleButtonGroup
        size="small"
        exclusive
        value={modalidade}
        onChange={(_, v) => setModalidade(v ?? '')}
        aria-label={tt('Filtrar por modalidade')}
        sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { whiteSpace: 'nowrap' } }}
      >
        <ToggleButton value="">{tt('Todas')}</ToggleButton>
        {CARROCERIAS.map((code) => (
          <ToggleButton key={code} value={code}>
            {categoryLabel({ code, label: code }, t)}
          </ToggleButton>
        ))}
        {/* Catálogo ainda carregando devolve lista vazia, e um botão de valor vazio
            disputaria o estado selecionado com "Todas". */}
        {outras && <ToggleButton value={outras}>{tt('Outras')}</ToggleButton>}
        <ToggleButton value={SEM_MODALIDADE}>{tt('Sem modalidade')}</ToggleButton>
      </ToggleButtonGroup>
    ),
  }
}

export const caminhoesConfig: ResourceConfig<ConjuntoCaminhao> = {
  title: 'Caminhões', singular: 'Conjunto', eyebrow: 'Conjuntos', exportPrefix: 'conjuntos-caminhao',
  basePath: '/logistica/conjuntos/caminhoes',
  createPermission: 'sets.create', editPermission: 'sets.edit', deletePermission: 'sets.delete',
  mirror: true,
  // O conjunto nasce da sincronização da frota, e o motorista dele do titular
  // do ERP: o botão roda os dois, na ordem do ciclo.
  protheusSync: ['motoristas', 'frota'], Banner: FrotaProtheusBanner,
  labelOf: (c) => c.name ?? c.driver?.name ?? c.uuid,
  resource: conjuntosCaminhaoResource,
  columns: [
    { key: 'name', header: 'Conjunto', render: (c) => c.name ?? '—' },
    { key: 'driver', header: 'Motorista', render: (c) => c.driver?.name ?? '—' },
    // Situação do motorista ao lado do nome: é a pergunta que a escala faz antes
    // de contar com o conjunto. Sem motorista não há situação a mostrar.
    {
      key: 'driver_situacao',
      header: 'Situação',
      render: (c) => (c.driver
        ? <SituacaoPessoa situacao={c.driver.situacao} label={c.driver.situacao_label} ausenciaAte={c.driver.ausencia_ate} />
        : <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>),
    },
    { key: 'truck', header: 'Cavalo', render: (c) => <FleetPlate plate={c.truck?.plate} category={c.truck?.category} /> },
    { key: 'modalidade', header: 'Modalidade', render: (c) => <ModalidadeDoCavalo categoria={c.truck?.category} /> },
    {
      key: 'trailers',
      header: 'Carretas',
      render: (c) => {
        const carretas = [c.trailer1, c.trailer2].filter((t) => t?.plate)
        if (!carretas.length) return <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            {carretas.map((t) => <FleetPlate key={t!.uuid} plate={t!.plate} category={t!.category} />)}
          </Box>
        )
      },
    },
    { key: 'active', header: 'Ativo', render: (c) => <DeclarativeText value={c.active ? 'Sim' : 'Não'} /> },
  ],
  searchable: true,
  countLabel: 'Conjuntos',
  useFilters: comporFiltros(
    makeSituacaoFilter('driver_situacao', 'Situação do motorista', 'Sem motorista'),
    useModalidadeDoCavalo,
  ),
  schema: caminhaoSchema,
  emptyForm: { driver_uuid: '', truck_uuid: '', trailer1_uuid: '', trailer2_uuid: '', name: '', active: true, notes: '' },
  toForm: (c) => ({
    driver_uuid: c.driver?.uuid ?? '', truck_uuid: c.truck?.uuid ?? '',
    trailer1_uuid: c.trailer1?.uuid ?? '', trailer2_uuid: c.trailer2?.uuid ?? '',
    name: c.name ?? '', active: c.active, notes: c.notes ?? '',
  }),
  // Espelho: só o que ainda é da operação viaja no PATCH. O servidor descarta o
  // resto de qualquer jeito (_CAMPOS_LIVRES em conjuntos_caminhao.py); não enviar
  // evita que a prévia da requisição sugira o contrário.
  toBody: (v) => ({
    notes: v.notes || null,
  }),
  Fields: CaminhaoFields,
}

/** Caminhões 2 Reboques (Samuel, 08/10/2026): "todos os conjuntos que o cavalo está
 *  vinculado em 2 carretas", o rodotrem que o ERP publica em duas linhas. É a grade
 *  de Caminhões com o recorte fixo do servidor, `reboques=2`, que vale também para o
 *  Baixar Excel. */
function useSoDoisReboques(): ResourceFilters {
  return { params: { reboques: '2' } }
}

export const caminhoes2ReboquesConfig: ResourceConfig<ConjuntoCaminhao> = {
  ...caminhoesConfig,
  title: 'Caminhões 2 Reboques',
  exportPrefix: 'conjuntos-caminhao-2-reboques',
  basePath: '/logistica/conjuntos/caminhoes-2-reboques',
  useFilters: comporFiltros(
    useSoDoisReboques,
    makeSituacaoFilter('driver_situacao', 'Situação do motorista', 'Sem motorista'),
    useModalidadeDoCavalo,
  ),
}

// ── Maquinários ──────────────────────────────────────────────────────
const maquinarioSchema = z.object({
  operator_uuid: z.string().optional(), machine_uuid: z.string().optional(),
  platform_uuid: z.string().optional(),
  name: z.string().optional(), active: z.boolean().optional(), notes: z.string().optional(),
})
function MaquinarioFields() {
  const operators = withEmpty(usePersonOptions('operator'))
  const machines = withEmpty(useMachineOptions())
  const plataformas = withEmpty(useFleetOptions('plataforma'))
  return (
    <>
      <RHFSelect name="operator_uuid" label="Operador" loadOptions={operatorOptionsLoader} selectedOptions={operators} asyncKey="maquinario:operators" placeholder="Buscar operador…" />
      <RHFSelect name="machine_uuid" label="Máquina" loadOptions={machineOptionsLoader} selectedOptions={machines} asyncKey="maquinario:machines" placeholder="Buscar máquina…" />
      {/* A plataforma (04/09/2026): o tipo de veículo cadastrado à mão que
          acompanha a máquina. */}
      <RHFSelect name="platform_uuid" label="Plataforma" loadOptions={platformOptionsLoader} selectedOptions={plataformas} asyncKey="maquinario:platforms" placeholder="Buscar plataforma…" />
      {/* Sempre derivado, também na edição (Samuel, 15/10/2026). */}
      <RHFText name="name" label="Nome, derivado do operador e da máquina" disabled />
      <RHFSwitch name="active" label="Ativo" />
      <RHFText name="notes" label="Observações" multiline rows={2} />
    </>
  )
}
export const maquinariosConfig: ResourceConfig<ConjuntoMaquinario> = {
  title: 'Máquinas', singular: 'Conjunto', eyebrow: 'Conjuntos', exportPrefix: 'conjuntos-maquinario',
  basePath: '/logistica/conjuntos/maquinas',
  // As de Conjuntos desde 15/10/2026; antes eram as do Transporte Máquina.
  createPermission: 'sets.create', editPermission: 'sets.edit', deletePermission: 'sets.delete',
  labelOf: (c) => c.name ?? c.operator?.name ?? c.uuid,
  resource: conjuntosMaquinarioResource,
  columns: [
    { key: 'name', header: 'Conjunto', render: (c) => c.name ?? '—' },
    { key: 'operator', header: 'Operador', render: (c) => c.operator?.name ?? '—' },
    // Mesma coluna do conjunto de caminhão, pela mesma razão. Aqui NÃO há
    // modalidade: máquina não puxa carroceria.
    {
      key: 'operator_situacao',
      header: 'Situação',
      render: (c) => (c.operator
        ? <SituacaoPessoa situacao={c.operator.situacao} label={c.operator.situacao_label} ausenciaAte={c.operator.ausencia_ate} />
        : <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>),
    },
    { key: 'machine', header: 'Máquina', render: (c) => <FleetPlate plate={c.machine?.name ?? c.machine?.plate} category={c.machine?.category} /> },
    { key: 'platform', header: 'Plataforma', render: (c) => (c.platform ? <FleetPlate plate={c.platform.plate ?? c.platform.name} category={c.platform.category} /> : <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>) },
    { key: 'active', header: 'Ativo', render: (c) => <DeclarativeText value={c.active ? 'Sim' : 'Não'} /> },
  ],
  searchable: true,
  countLabel: 'Conjuntos',
  useFilters: makeSituacaoFilter('operator_situacao', 'Situação do operador', 'Sem operador'),
  schema: maquinarioSchema,
  emptyForm: { operator_uuid: '', machine_uuid: '', platform_uuid: '', name: '', active: true, notes: '' },
  toForm: (c) => ({
    operator_uuid: c.operator?.uuid ?? '', machine_uuid: c.machine?.uuid ?? '',
    platform_uuid: c.platform?.uuid ?? '',
    name: c.name ?? '', active: c.active, notes: c.notes ?? '',
  }),
  toBody: (v) => ({
    operator_uuid: v.operator_uuid || null, machine_uuid: v.machine_uuid || null,
    platform_uuid: v.platform_uuid || null,
    name: v.name || null, active: Boolean(v.active), notes: v.notes || null,
  }),
  Fields: MaquinarioFields,
}

// ── Utilitários: operador mais veículo utilitário (04/09/2026) ────────────
const utilitarioSchema = z.object({
  operator_uuid: z.string().optional(), vehicle_uuid: z.string().optional(),
  name: z.string().optional(), active: z.boolean().optional(), notes: z.string().optional(),
})
function UtilitarioFields() {
  const operators = withEmpty(usePersonOptions('operator'))
  const utilitarios = withEmpty(useFleetOptions('utilitario'))
  return (
    <>
      <RHFSelect name="operator_uuid" label="Operador" loadOptions={operatorOptionsLoader} selectedOptions={operators} asyncKey="utilitario:operators" placeholder="Buscar operador…" />
      <RHFSelect name="vehicle_uuid" label="Veículo utilitário" loadOptions={utilityVehicleOptionsLoader} selectedOptions={utilitarios} asyncKey="utilitario:vehicles" placeholder="Buscar placa do utilitário…" />
      <RHFText name="name" label="Nome, derivado do operador e da placa" disabled />
      <RHFSwitch name="active" label="Ativo" />
      <RHFText name="notes" label="Observações" multiline rows={2} />
    </>
  )
}
export const utilitariosConfig: ResourceConfig<ConjuntoUtilitario> = {
  title: 'Utilitários', singular: 'Conjunto', eyebrow: 'Conjuntos', exportPrefix: 'conjuntos-utilitario',
  basePath: '/logistica/conjuntos/utilitarios',
  createPermission: 'sets.create', editPermission: 'sets.edit', deletePermission: 'sets.delete',
  labelOf: (c) => c.name ?? c.operator?.name ?? c.uuid,
  resource: conjuntosUtilitarioResource,
  columns: [
    { key: 'name', header: 'Conjunto', render: (c) => c.name ?? '—' },
    { key: 'operator', header: 'Operador', render: (c) => c.operator?.name ?? '—' },
    { key: 'vehicle', header: 'Utilitário', render: (c) => <FleetPlate plate={c.vehicle?.plate ?? c.vehicle?.name} category={c.vehicle?.category} /> },
    { key: 'active', header: 'Ativo', render: (c) => <DeclarativeText value={c.active ? 'Sim' : 'Não'} /> },
  ],
  searchable: true,
  schema: utilitarioSchema,
  emptyForm: { operator_uuid: '', vehicle_uuid: '', name: '', active: true, notes: '' },
  toForm: (c) => ({
    operator_uuid: c.operator?.uuid ?? '', vehicle_uuid: c.vehicle?.uuid ?? '',
    name: c.name ?? '', active: c.active, notes: c.notes ?? '',
  }),
  toBody: (v) => ({
    operator_uuid: v.operator_uuid || null, vehicle_uuid: v.vehicle_uuid || null,
    name: v.name || null, active: Boolean(v.active), notes: v.notes || null,
  }),
  Fields: UtilitarioFields,
}
